import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';

import { AgentAvailability } from 'models/agent_status';
import AgentOnCallWarning from 'models/modal/agent_on_call_warning';
import Checkmark from 'components/common/icons/checkmark';
import connect from 'components/lib/connect';
import DropdownMenu, { ChevronIcon, HeaderItem, LabelContainer, OpenerButton } from 'components/common/dropdown_menu';
import Menu from 'components/common/menu';
import OpenModal from 'actions/modal/open_modal';
import i18n from 'scripts/i18n/i18n';
import { ReasonType } from 'models/agent_status_reason';
import SignOut from 'components/common/icons/sign_out';
import UpdateAgentStatus from 'actions/liveboards/update_agent_status';
import { useExecuteAction } from 'components/hooks/connect_hooks';

const LOG_OUT_ID = 'log_out';

export function AgentStatusReasonSelector({
  agentId,
  awayReasonId,
  awayReasonText,
  className,
  isAgentOnCall,
  reasons,
  setLastUsedAwayReasonId,
}) {
  const executeAction = useExecuteAction();

  const onSelect = useCallback(
    value => {
      if (value !== awayReasonId) {
        const isLogOut = value === LOG_OUT_ID;
        if (isAgentOnCall && !isLogOut) {
          executeAction(OpenModal, new AgentOnCallWarning({ agentId, reasonId: value }));
          setLastUsedAwayReasonId?.(value);
          return;
        }
        executeAction(UpdateAgentStatus, {
          agentId,
          newStatus: isLogOut ? AgentAvailability.OFFLINE : AgentAvailability.AWAY,
          reasonId: isLogOut ? null : value,
        });
        setLastUsedAwayReasonId?.(isLogOut ? null : value);
      }
    },
    [agentId, awayReasonId, isAgentOnCall, executeAction, setLastUsedAwayReasonId]
  );

  if (!reasons.length) {
    return null;
  }

  return (
    <DropdownMenu
      className={className}
      menuComponent={StyledMenu}
      onSelect={onSelect}
      openerComponent={StyledOpenerButton}
      options={reasons}
      placeholder="Select an Away Status"
      position="bottom"
      searchable
      text={awayReasonText}
    />
  );
}

AgentStatusReasonSelector.propTypes = {
  agentId: PropTypes.string.isRequired,
  awayReasonId: PropTypes.string,
  awayReasonText: PropTypes.string,
  className: PropTypes.string,
  isAgentOnCall: PropTypes.bool,
  reasons: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.exact({
        label: PropTypes.node,
        type: PropTypes.oneOf(['HEADER', 'SPLITTER']).isRequired,
      }),
      PropTypes.exact({
        label: PropTypes.node.isRequired,
        text: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
      }),
    ])
  ).isRequired,
  setLastUsedAwayReasonId: PropTypes.func,
};

function mapStateToProps(
  { getProvider },
  {
    agentId,
    awayReasonId,
    awayReasonsHeader,
    className,
    includeLogOut,
    lastUsedAwayReasonId,
    isAgentOnCall,
    setLastUsedAwayReasonId,
    text,
  }
) {
  const reasons = [];
  let awayReasonText = text;

  if (includeLogOut) {
    reasons.push({
      value: LOG_OUT_ID,
      text: 'Log out',
      label: (
        <ReasonContainer>
          <ReasonLabel>{'Log out'}</ReasonLabel>
          <ReasonIcon>
            <LogOutIcon />
          </ReasonIcon>
        </ReasonContainer>
      ),
    });
  }

  const agentStatusReasonsProvider = getProvider('agentStatusReasons');

  let addAwayReasonsHeader = true;
  if (lastUsedAwayReasonId) {
    const lastUsedAwayReason = agentStatusReasonsProvider.findBy({ id: lastUsedAwayReasonId });
    if (lastUsedAwayReason) {
      if (reasons.length > 0) {
        reasons.push({ label: 'splitter_last_used', type: 'SPLITTER' });
      }
      reasons.push(
        { label: awayReasonsHeader, type: 'HEADER' },
        {
          value: lastUsedAwayReason.id,
          text: lastUsedAwayReason.name,
          label: (
            <ReasonContainer>
              <ReasonTagLabel>{lastUsedAwayReason.name}</ReasonTagLabel>
              <ReasonTag>{i18n.t('Last used')}</ReasonTag>
            </ReasonContainer>
          ),
        }
      );
      addAwayReasonsHeader = false;
    }
  }

  if (awayReasonId) {
    const currentAwayReason = agentStatusReasonsProvider.findBy({ id: awayReasonId });
    if (currentAwayReason) {
      if (reasons.length > 0) {
        reasons.push({ label: 'splitter_current_reason', type: 'SPLITTER' });
      }
      if (addAwayReasonsHeader) {
        reasons.push({ label: awayReasonsHeader, type: 'HEADER' });
        addAwayReasonsHeader = false;
      }
      reasons.push({
        value: currentAwayReason.id,
        text: currentAwayReason.name,
        label: (
          <ReasonContainer>
            <ReasonLabel>{currentAwayReason.name}</ReasonLabel>
            <ReasonIcon>
              <ActiveCheckmarkIcon />
            </ReasonIcon>
          </ReasonContainer>
        ),
      });
      awayReasonText = currentAwayReason.name;
    }
  }

  const awayReasons = agentStatusReasonsProvider.findAll({
    filter: r =>
      !r.disabled &&
      r.type === ReasonType.AWAY &&
      (!awayReasonId || r.id !== awayReasonId) &&
      (!lastUsedAwayReasonId || r.id !== lastUsedAwayReasonId),
  });
  if (awayReasons?.length > 0) {
    if (reasons.length === 1) {
      reasons.push({ label: 'splitter_other_reasons', type: 'SPLITTER' });
    }
    if (addAwayReasonsHeader) {
      reasons.push({ label: awayReasonsHeader, type: 'HEADER' });
    }
    reasons.push(
      ...awayReasons
        .map(r => ({ value: r.id, label: r.name }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
    );
  }

  return { agentId, awayReasonId, awayReasonText, className, isAgentOnCall, reasons, setLastUsedAwayReasonId };
}

const AgentStatusReasonSelectorContainer = connect(mapStateToProps)(AgentStatusReasonSelector);
export default AgentStatusReasonSelectorContainer;

const ActiveCheckmarkIcon = styled(Checkmark)`
  height: ${p => p.theme.spacing.large};
  width: ${p => p.theme.spacing.large};
  fill: ${p => p.theme.colors.green400};
`;

const ReasonContainer = styled.div`
  width: 100%;
  display: flex;
`;

const ReasonIcon = styled.div`
  display: flex;
  align-items: center;
`;

const ReasonLabel = styled.div`
  width: calc(100% - ${p => p.theme.spacing.large});
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReasonTag = styled.span`
  margin-left: ${p => p.theme.spacing.medium};
  color: ${p => p.theme.colors.gray700};
  font-size: ${p => p.theme.fontSize.reduced};
  font-style: italic;
`;

const ReasonTagLabel = styled.span`
  width: auto;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LogOutIcon = styled(SignOut)`
  height: ${p => p.theme.spacing.large};
  width: ${p => p.theme.spacing.large};
`;

const StyledOpenerButton = styled(OpenerButton)`
  max-width: 100%;

  ${p => (p.isOpen ? { 'background-color': p.theme.colors.gray300 } : { 'background-color': 'transparent' })}
  border: none;
  font-weight: normal;

  ${ChevronIcon} {
    ${p => (p.isOpen ? '' : { visibility: 'hidden' })}
  }

  &:hover {
    background-color: ${p => p.theme.colors.green400};
    color: ${p => p.theme.colors.white};
    padding: 8px 16px;
    ${ChevronIcon} {
      visibility: visible;
      stroke: ${p => p.theme.colors.white};
      fill: ${p => p.theme.colors.white};
    }
  }
`;

const StyledMenu = styled(Menu)`
  ${HeaderItem} {
    font-size: ${p => p.theme.fontSize.base};
    padding: 8px 24px;
  }

  ${LabelContainer} {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
